import { Hammer, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import type { ToolHandoffOrigin, ForgeWorkOrigin } from '@shared/types/continuations';
import {
  buildResultToolCreationPrompt,
  navigateToFlowComposerPrompt,
} from '@/lib/flow-composer-handoff';

export interface ForgeHandoffSelection {
  actionLabel: string;
  summary: string | null;
  seedPrompt: string;
  origin?: ToolHandoffOrigin | ForgeWorkOrigin | null;
}

interface ActivityForgeHandoffDialogProps {
  selection: ForgeHandoffSelection | null;
  onClose: () => void;
}

/**
 * Home-level confirmation before leaving for the Forge: a complex action that
 * has no ready tool is proposed as an explicit, reusable tool creation instead
 * of silently navigating away from the accounts page.
 */
export const ActivityForgeHandoffDialog = ({
  selection,
  onClose,
}: ActivityForgeHandoffDialogProps) => {
  const navigate = useNavigate();

  if (!selection) return null;

  const openForge = () => {
    const prompt = buildResultToolCreationPrompt(selection.seedPrompt);
    if (!prompt) return;
    onClose();
    navigateToFlowComposerPrompt(navigate, prompt, selection.origin);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-domino-aubergine/35 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="forge-handoff-dialog-title"
        className="w-full max-w-xl overflow-hidden rounded-[30px] border border-domino-cardBorder bg-domino-warm shadow-[0_28px_90px_rgba(59,23,63,0.24)]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-domino-cardBorder bg-domino-white px-5 py-5 lg:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-domino-aubergine text-white">
              <Hammer className="h-4 w-4" />
            </div>
            <div>
              <h2
                id="forge-handoff-dialog-title"
                className="mt-1 text-2xl font-semibold tracking-tight text-domino-ink"
              >
                Faire réutiliser ce travail
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer la proposition Forge"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-domino-cardBorder bg-domino-warm text-domino-inkSoft transition hover:border-domino-prune hover:text-domino-aubergine focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5 lg:px-6">
          <p className="text-sm leading-6 text-domino-inkSoft">
            Domino transmet le besoin, l’exemple et ses contraintes à la Forge.
            La personne qui configure votre espace pourra l’adapter, le tester puis le rendre disponible à l’équipe.
          </p>

          <div className="rounded-[22px] border border-domino-cardBorder bg-domino-white px-4 py-3">
            <p className="text-sm font-semibold text-domino-ink">
              {selection.actionLabel}
            </p>
            {selection.summary && (
              <p className="mt-1 text-xs leading-5 text-domino-inkSoft">
                {selection.summary}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-domino-cardBorder bg-domino-white px-5 py-4 lg:px-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button type="button" onClick={openForge} className="gap-2">
            <Hammer className="h-4 w-4" />
            Préparer dans la Forge
          </Button>
        </div>
      </div>
    </div>
  );
};
